import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import { useLatestRequest } from "../../../hooks/useLatestRequest";
import { createAttendanceListRequest, getAttendanceList } from "../api/attendance.api";
import {
  defaultAttendanceFilters,
  type AttendanceFilters,
  type AttendanceItem,
} from "../types/attendance.types";

const getErrorMessage = (error: unknown) =>
  error instanceof Error ? error.message : "Failed to load attendances";

export const useGetAttendanceList = () => {
  const [attendances, setAttendances] = useState<AttendanceItem[]>([]);
  const [filters, setFilters] = useState<AttendanceFilters>(defaultAttendanceFilters);
  const [debouncedFilters, setDebouncedFilters] =
    useState<AttendanceFilters>(defaultAttendanceFilters);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const { run, loading } = useLatestRequest();

  const hasActiveFilters = useMemo(
    () =>
      Object.entries(filters).some(([key, value]) =>
        key === "rewarded_points"
          ? value !== null
          : typeof value === "string" && value.trim() !== "" && value !== "all",
      ),
    [filters],
  );

  const fetchAttendances = useCallback(
    async (requestFilters = debouncedFilters, requestPage = page) => {
      setError(null);
      try {
        const response = await run((signal) =>
          getAttendanceList(createAttendanceListRequest(requestFilters, requestPage), signal),
        );
        if (!response) return;
        setAttendances(response.list);
        setTotalCount(response.paging.count);
      } catch (requestError: unknown) {
        setError(getErrorMessage(requestError));
      }
    },
    [debouncedFilters, page, run],
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setPage(1);
      setDebouncedFilters(filters);
    }, 500);
    return () => window.clearTimeout(timer);
  }, [filters]);

  useEffect(() => {
    void fetchAttendances();
  }, [debouncedFilters, page, fetchAttendances]);

  const refreshAttendances = useCallback(async () => {
    const nextFilters = defaultAttendanceFilters;
    setFilters(nextFilters);
    setDebouncedFilters(nextFilters);
    setPage(1);
    await fetchAttendances(nextFilters, 1);
  }, [fetchAttendances]);

  return {
    attendances,
    loading,
    error,
    filters,
    setFilters: setFilters as Dispatch<SetStateAction<AttendanceFilters>>,
    page,
    setPage,
    totalCount,
    hasActiveFilters,
    refreshAttendances,
  };
};
